import {
  faFile,
  faTrash,
  faDownload,
  faEye,
  faExclamationTriangle,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { useCallback } from "react";

import { useFile } from "src/api/filesApi/queries";
import { Icon } from "src/base-components/Icon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  type OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { type FileCategoryData } from "src/cases/caseView/FileCategory";
import { GetFileEnvFilesFileIdGetEnvEnum } from "src/clients/files";
import { Tooltip } from "src/design-system/Tooltip";
import { FileViewer } from "src/files/FileViewer";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { formatDateWithThreshold } from "src/utils/datetime";
import { FILE_TYPES, humanReadableSize } from "src/utils/files";

type FileCardProps = {
  fileId: string;
  env: GetFileEnvFilesFileIdGetEnvEnum;
  categoryKey: string;
  fileCategories: FileCategoryData[];
  onDeleteFile: (fileId: string, fileName: string, categoryKey: string) => void;
  onMoveFile: (
    fileId: string,
    fileName: string,
    fromCategory: string,
    toCategory: string,
  ) => void;
  canEdit: boolean;
  canDelete: boolean;
  isNewlyUploaded?: boolean;
};

export const FileCard: React.FC<FileCardProps> = ({
  fileId,
  env,
  categoryKey,
  fileCategories,
  onDeleteFile,
  onMoveFile,
  canEdit,
  canDelete,
  isNewlyUploaded = false,
}) => {
  const { workspace } = useWorkspaceContext();

  const {
    data: fileResponse,
    isLoading,
    error,
  } = useFile(fileId, env, workspace.base_url);

  const file = fileResponse?.data?.data;

  const getDropdownElements = useCallback(
    (mode: "full" | "delete-only" = "full"): OptionsDropdownElement[] => {
      const fileName = file?.attributes?.name || "Unknown file";

      const deleteOption: OptionsDropdownElement = {
        key: "Delete file",
        action: () => onDeleteFile(fileId, fileName, categoryKey),
        icon: faTrash,
      };

      if (mode === "delete-only") {
        return canDelete ? [deleteOption] : [];
      }

      const otherCategories = fileCategories.filter(
        (cat) => cat.category_key !== categoryKey,
      );

      const elements: OptionsDropdownElement[] = [];

      // Add move options if user can edit and there are other categories
      if (canEdit && otherCategories.length > 0) {
        elements.push(
          { key: "header", header: "Move to:" } as OptionsDropdownElement,
          ...otherCategories.map((cat) => ({
            key: cat.display_name,
            action: () =>
              onMoveFile(fileId, fileName, categoryKey, cat.category_key),
          })),
        );
      }

      // Add delete option if user can delete
      if (canDelete) {
        if (elements.length > 0) {
          elements.push(MENU_DIVIDER);
        }
        elements.push(deleteOption);
      }

      return elements;
    },
    [
      fileCategories,
      categoryKey,
      onMoveFile,
      fileId,
      onDeleteFile,
      file?.attributes?.name,
      canEdit,
      canDelete,
    ],
  );
  const isFileNotFound = isAxiosError(error) && error.response?.status === 404;

  if (isLoading) {
    return (
      <div
        className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white p-3"
        data-loc={`file-card-loading-${fileId}`}
      >
        <SkeletonPlaceholder height="h-4" width="w-24" />
      </div>
    );
  }

  if (isFileNotFound || (!file && !isLoading)) {
    return (
      <div
        className="flex w-full justify-between rounded-lg border border-gray-200 bg-white p-2"
        data-loc={`file-card-error-${fileId}`}
      >
        <div className="flex gap-1">
          <Icon color="text-red-600" icon={faExclamationTriangle} size="xs" />
          <div className="flex flex-col">
            <span
              className="text-gray-800 font-inter-semibold-13px"
              data-loc="file-not-found-title"
            >
              File not found
            </span>
            <span
              className="text-gray-500 font-inter-normal-12px"
              data-loc="file-not-found-message"
            >
              File load failed — it might be missing or broken.
            </span>
          </div>
        </div>
        {canDelete && (
          <EllipsisOptionsDropdown
            dataLoc={`file-options-error-${fileId}`}
            elements={getDropdownElements("delete-only")}
            iconSize="xs"
          />
        )}
      </div>
    );
  }

  if (!file) {
    return null;
  }

  const fileType =
    FILE_TYPES[file.attributes?.content_type as keyof typeof FILE_TYPES] ||
    "File";

  const metadata = [
    fileType,
    file.attributes?.size && humanReadableSize(file.attributes.size),
    file.attributes?.created_at &&
      `Added ${formatDateWithThreshold(file.attributes.created_at, 30, "MMM d, yyyy")}`,
  ]
    .filter(Boolean)
    .join(" • ");

  const dropdownElements = getDropdownElements("full");
  const showFileOptions = dropdownElements.length > 0;

  return (
    <div
      className="flex w-full justify-between rounded-lg border border-gray-200 bg-white p-2"
      data-loc={`file-card-${fileId}`}
    >
      <div className="flex gap-1">
        <Icon color="text-indigo-500" icon={faFile} size="xs" />
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Tooltip title={file.attributes?.name || "Unknown file"} asChild>
              <span
                className="line-clamp-1 text-gray-800 font-inter-semibold-13px"
                data-loc={`file-name-${fileId}`}
              >
                {file.attributes?.name || "Unknown file"}
              </span>
            </Tooltip>

            {isNewlyUploaded && (
              <Pill
                data-loc={`file-new-pill-${fileId}`}
                size="sm"
                variant="indigo-light"
              >
                <Pill.Text>New</Pill.Text>
              </Pill>
            )}
          </div>
          <span
            className="text-gray-500 font-inter-normal-12px"
            data-loc={`file-metadata-${fileId}`}
          >
            {metadata}
          </span>
        </div>
      </div>
      {file.attributes && (
        <div className="flex gap-1">
          <FileViewer file={file.attributes}>
            <Icon
              color="text-gray-500"
              data-loc={`file-view-${fileId}`}
              icon={faEye}
              size="xs"
            />
          </FileViewer>
          <a
            aria-label="Download file"
            data-loc={`file-download-${fileId}`}
            download={file.attributes?.name || "file"}
            href={file.attributes?.download_url || "#"}
          >
            <Icon color="text-gray-500" icon={faDownload} size="xs" />
          </a>
          {showFileOptions && (
            <EllipsisOptionsDropdown
              dataLoc={`file-options-${fileId}`}
              elements={dropdownElements}
              iconSize="xs"
            />
          )}
        </div>
      )}
    </div>
  );
};
